import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

/**
 * Store product upload (flag `store_product_upload`). The routes are
 * `requireFlag`-gated on the server; callers render only while the flag is on.
 * Products a store uploads stay private to that store.
 */

export type StoreProductUploadStatus = 'matched' | 'created' | 'updated' | 'rejected';

export interface StoreProductUploadRow {
  row: number;
  status: StoreProductUploadStatus;
  brand?: string;
  model?: string;
  productId?: string;
  matchedBy?: 'barcode' | 'sku' | 'model';
  reason?: string;
  skuLinked?: boolean;
  warnings: string[];
}

export interface StoreProductUploadResult {
  total: number;
  matched: number;
  created: number;
  updated: number;
  rejected: number;
  rows: StoreProductUploadRow[];
  pricing: { rows: number; status: 'applied' | 'failed' | 'none'; submissionId?: string; message?: string };
}

export interface StoreProductInput {
  brand: string;
  model: string;
  name?: string;
  upc?: string;
  sku?: string;
  msrp?: string;
  map?: string;
  category?: string;
  description?: string;
  imageUrls?: string[];
}

export interface MyStoreProduct {
  id: string;
  name: string;
  brand: string;
  model: string;
  sku: string | null;
  upc: string | null;
  category: string;
  /** Set when the product was submitted to Motzu (flag `product_submissions`) rather than kept private. */
  submissionId?: string | null;
}

const failure = async (response: Response, fallback: string): Promise<never> => {
  let message = fallback;
  try {
    const body = await response.json();
    message = body?.message || body?.error || fallback;
  } catch {
    // keep the fallback
  }
  throw new Error(message);
};

export const storeProductUploadApi = {
  async uploadCsv(file: File): Promise<StoreProductUploadResult> {
    const headers = (await getAuthHeaders()) as Record<string, string>;
    delete headers['Content-Type'];
    const body = new FormData();
    body.append('file', file);
    const response = await makeApiCall('/store/products/upload', { method: 'POST', headers, body });
    if (!response.ok) return failure(response, 'Failed to upload products');
    return (await response.json()).data;
  },

  async createOne(input: StoreProductInput): Promise<StoreProductUploadResult> {
    const response = await makeApiCall('/store/products', {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) return failure(response, 'Failed to add the product');
    return (await response.json()).data;
  },

  async downloadTemplate(): Promise<void> {
    const response = await makeApiCall('/store/products/upload/template', { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to download the template');
    const url = URL.createObjectURL(await response.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'store-products-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  },

  async listMine(search?: string): Promise<MyStoreProduct[]> {
    const query = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const response = await makeApiCall(`/store/products/mine${query}`, { headers: await getAuthHeaders() });
    if (!response.ok) return failure(response, 'Failed to load your uploaded products');
    return (await response.json()).data || [];
  },

  async select(productIds: string[]): Promise<{ added: string[]; skipped: string[] }> {
    const response = await makeApiCall('/store/products/mine/select', {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds }),
    });
    if (!response.ok) return failure(response, 'Failed to add products to the sync selection');
    return (await response.json()).data;
  },
};
